import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, qs } from '../lib/api.js';
import { useAction, useResource } from '../lib/useApi.js';
import { useAuth } from '../lib/auth.js';
import type { DeploymentDetail, DeploymentItem, ItemStatus } from '../lib/types.js';
import {
  Banner,
  Card,
  Empty,
  ErrorState,
  Loading,
  Modal,
  Pill,
  Stat,
  StatusPill,
  When,
  useToast,
} from '../components/ui.js';
import { OutcomeBar } from './Deployments.js';

const ITEM_FILTERS: Array<ItemStatus | ''> = [
  '',
  'SUCCEEDED',
  'FAILED',
  'SKIPPED',
  'NEEDS_CONSENT',
  'DRY_RUN',
  'PENDING',
];

/**
 * Deployment log: who received the update, when, whether it worked, and the
 * controls to retry the failures or revert the whole thing.
 */
export function DeploymentDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const { me } = useAuth();
  const toast = useToast();

  const [itemStatus, setItemStatus] = useState<ItemStatus | ''>('');
  const [search, setSearch] = useState('');
  const [confirmRollback, setConfirmRollback] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [inspecting, setInspecting] = useState<DeploymentItem | null>(null);

  const deployment = useResource<DeploymentDetail>(`/api/deployments/${id}`, { pollMs: 4000 });
  const items = useResource<{ items: DeploymentItem[]; total: number }>(
    `/api/deployments/${id}/items${qs({ status: itemStatus, search, take: 250 })}`,
    { pollMs: deployment.data?.isRunning ? 4000 : undefined },
  );

  const canRetry = Boolean(me?.permissions.includes('deploy:retry'));
  const canRollback = Boolean(me?.capabilities.canRollback);
  const canCancel = Boolean(me?.permissions.includes('deploy:cancel'));

  const retry = useAction(async () => {
    const result = await api.post<{ retried: number; message: string }>(
      `/api/deployments/${id}/retry`,
    );
    toast.success('Retrying', result.message);
    await Promise.all([deployment.reload(), items.reload()]);
  });

  const removeSignature = useAction(async () => {
    await api.post(`/api/deployments/${id}/remove-signature`, {});
    setConfirmRemove(false);
    await deployment.reload();
    toast.success('Signature removed', 'The rule is gone. Mail goes out unsigned from the next message.');
  });

  const rollback = useAction(async () => {
    const result = await api.post<{ deploymentId: string; message: string }>(
      `/api/deployments/${id}/rollback`,
    );
    setConfirmRollback(false);
    toast.success('Rolled back', result.message);
    await deployment.reload();
  });

  const cancel = useAction(async () => {
    await api.post(`/api/deployments/${id}/cancel`);
    toast.success('Cancelled');
    await deployment.reload();
  });

  if (deployment.error) {
    return (
      <div className="page-body">
        <ErrorState error={deployment.error} onRetry={deployment.reload} />
      </div>
    );
  }
  if (!deployment.data) {
    return (
      <div className="page-body">
        <Loading rows={5} label="Loading deployment" />
      </div>
    );
  }

  const d = deployment.data;

  return (
    <>
      <header className="page-head">
        <div>
          <div className="row wrap" style={{ marginBottom: 2 }}>
            <Link to="/deployments" className="tiny muted">
              Deployments
            </Link>
            <span className="tiny muted">/</span>
            <h1>{d.template?.name ?? 'Deployment'}</h1>
            <StatusPill status={d.status} />
            {d.dryRun && <Pill tone="accent">dry run</Pill>}
            {d.kind !== 'PUSH' && <Pill tone="outline">{d.kind.toLowerCase()}</Pill>}
          </div>
          <p className="sub">
            {d.targetLabel ?? d.targetType} · v{d.version?.version} · via{' '}
            <span className="mono">{d.providerKey}</span> · started by {d.createdBy?.displayName}
          </p>
        </div>

        <div className="page-actions">
          {['SCHEDULED', 'QUEUED'].includes(d.status) && canCancel && (
            <button className="danger" disabled={cancel.pending} onClick={() => void cancel.run()}>
              Cancel
            </button>
          )}
          {d.canRetry && canRetry && (
            <button disabled={retry.pending} onClick={() => void retry.run()}>
              {retry.pending ? 'Retrying…' : `Retry ${d.failedCount} failure(s)`}
            </button>
          )}
          {d.canRollback && canRollback && (
            <>
              {/*
                Labelled by what it will actually do. "Roll back" reads as
                "remove the signature", which is only true for the push that
                created the rule -- for every push after that it reinstates the
                previous version. Same button, different outcome, so the label
                has to say which.
              */}
              <button className="danger" onClick={() => setConfirmRollback(true)}>
                {d.rollbackEffect === 'remove' ? 'Remove signature' : 'Restore previous version'}
              </button>
              {d.rollbackEffect === 'restore' && (
                <button className="danger" onClick={() => setConfirmRemove(true)}>
                  Remove signature
                </button>
              )}
            </>
          )}
        </div>
      </header>

      <div className="page-body">
        {retry.error && <ErrorState error={retry.error} />}
        {rollback.error && <ErrorState error={rollback.error} />}
        {removeSignature.error && <ErrorState error={removeSignature.error} />}
        {cancel.error && <ErrorState error={cancel.error} />}

        {d.error && (
          <Banner tone="danger">
            <strong>Deployment error</strong>
            <div>{d.error}</div>
          </Banner>
        )}

        {d.status === 'SCHEDULED' && (
          <Banner tone="info">
            <strong>
              Scheduled for {d.scheduledFor ? new Date(d.scheduledFor).toLocaleString() : 'later'}
            </strong>
            <div className="tiny">
              Directory attributes are read at run time, so it will use current data rather than
              whatever was true when it was queued.
            </div>
          </Banner>
        )}

        {d.expiresAt && !d.expiredAt && (
          <Banner tone="info">
            <strong>Temporary — ends {new Date(d.expiresAt).toLocaleString()}</strong>
            <div className="tiny">
              The scheduler will roll this back automatically, restoring exactly what was live
              before it ran. If that rollback fails it is not retried, so check back here on the
              day.
            </div>
          </Banner>
        )}

        {d.expiredAt && (
          <Banner tone="info">
            <strong>Ended {new Date(d.expiredAt).toLocaleString()}</strong>
            <div className="tiny">
              Its end date passed and an automatic rollback was attempted. The rollback below is
              the record of what happened — check it succeeded.
            </div>
          </Banner>
        )}

        {d.dryRun && ['SUCCEEDED', 'PARTIAL'].includes(d.status) && (
          <Banner tone="warn">
            <strong>This was a dry run — nothing changed</strong>
            <div className="tiny">
              The log below shows what would have happened. Re-run with dry run off to apply it.
            </div>
          </Banner>
        )}

        {d.rollbackOf && (
          <Banner tone="info">
            This is a rollback of <Link to={`/deployments/${d.rollbackOf.id}`}>an earlier deployment</Link>.
          </Banner>
        )}
        {d.rollbacks.length > 0 && (
          <Banner tone="warn">
            <strong>This deployment has been rolled back</strong>
            <div className="tiny">
              {d.rollbacks.map((r) => (
                <Link key={r.id} to={`/deployments/${r.id}`} style={{ marginRight: 8 }}>
                  rollback {r.status.toLowerCase()}
                </Link>
              ))}
            </div>
          </Banner>
        )}

        <div className="grid cols-4 mb">
          <Stat label="Targeted" value={d.totalCount} />
          <Stat
            label="Delivered"
            value={d.succeededCount}
            tone={d.succeededCount === d.totalCount ? 'ok' : undefined}
          />
          <Stat label="Failed" value={d.failedCount} tone={d.failedCount > 0 ? 'danger' : undefined} />
          <Stat
            label="Skipped"
            value={d.skippedCount}
            note={d.itemCounts.NEEDS_CONSENT ? `${d.itemCounts.NEEDS_CONSENT} need consent` : undefined}
          />
        </div>

        <Card
          title="Progress"
          actions={
            <span className="tiny muted">
              {d.startedAt && (
                <>
                  started <When iso={d.startedAt} />
                </>
              )}
              {d.finishedAt && (
                <>
                  {' · finished '}
                  <When iso={d.finishedAt} />
                </>
              )}
            </span>
          }
        >
          <OutcomeBar deployment={d} />
          {d.isRunning && (
            <p className="tiny muted mt">
              Running now — this page refreshes every few seconds.
            </p>
          )}
        </Card>

        {d.provider && (
          <Card title={`Mechanism: ${d.provider.displayName}`}>
            <p className="tiny">{d.provider.description}</p>
            <ul className="tiny muted" style={{ margin: 0, paddingLeft: 18 }}>
              {d.provider.capabilities.caveats.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </Card>
        )}

        {d.notes.length > 0 && (
          <Card title="What to do next">
            <ul style={{ margin: 0, paddingLeft: 18 }} className="tiny">
              {d.notes.map((note, i) => (
                <li key={i}>{note}</li>
              ))}
            </ul>
          </Card>
        )}

        <Card title="Per-user log" tight>
          <div className="toolbar" style={{ padding: '10px 12px', marginBottom: 0 }}>
            <div className="seg">
              {ITEM_FILTERS.map((s) => (
                <button
                  key={s || 'all'}
                  className={itemStatus === s ? 'on' : ''}
                  onClick={() => setItemStatus(s)}
                >
                  {s === '' ? 'All' : s.replace(/_/g, ' ').toLowerCase()}
                  {s && d.itemCounts[s] ? ` (${d.itemCounts[s]})` : ''}
                </button>
              ))}
            </div>
            <span className="spacer" />
            <input
              className="grow"
              placeholder="Filter by name or UPN"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 240 }}
            />
          </div>

          {items.error && <ErrorState error={items.error} />}
          {items.loading && !items.data && (
            <div style={{ padding: 14 }}>
              <Loading rows={3} />
            </div>
          )}

          {items.data && items.data.items.length === 0 && (
            <Empty title="No matching rows">
              {itemStatus ? `Nothing with status ${itemStatus.toLowerCase()}.` : 'No users recorded.'}
            </Empty>
          )}

          {items.data && items.data.items.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th className="num">Attempts</th>
                    <th>Detail</th>
                    <th>Finished</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.data.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="cell-strong">{item.displayName ?? item.upn}</div>
                        <div className="tiny mono muted">{item.upn}</div>
                      </td>
                      <td className="tiny">{item.department ?? <span className="muted">—</span>}</td>
                      <td>
                        <StatusPill status={item.status} />
                      </td>
                      <td className="num">{item.attempts}</td>
                      <td
                        className="tiny"
                        style={{
                          maxWidth: '38ch',
                          color: item.status === 'FAILED' ? 'var(--danger)' : undefined,
                        }}
                      >
                        {item.error ?? <span className="muted">—</span>}
                      </td>
                      <td className="tiny muted nowrap">
                        <When iso={item.finishedAt} />
                      </td>
                      <td className="right">
                        <button className="sm" onClick={() => setInspecting(item)}>
                          HTML
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {items.data && items.data.total > items.data.items.length && (
            <p className="tiny muted" style={{ padding: '10px 12px' }}>
              Showing {items.data.items.length} of {items.data.total}. Narrow the filter to see the rest.
            </p>
          )}
        </Card>

        {d.log.length > 0 && (
          <Card title="Run log">
            <pre className="log">{d.log.join('\n')}</pre>
          </Card>
        )}
      </div>

      {inspecting && (
        <ItemHtmlModal deploymentId={id} item={inspecting} onClose={() => setInspecting(null)} />
      )}

      {confirmRemove && (
        <Modal
          title="Remove the signature"
          onClose={() => setConfirmRemove(false)}
          footer={
            <>
              <button onClick={() => setConfirmRemove(false)}>Cancel</button>
              <button
                className="danger"
                disabled={removeSignature.pending}
                onClick={() => void removeSignature.run()}
              >
                {removeSignature.pending ? 'Removing…' : 'Remove signature'}
              </button>
            </>
          }
        >
          <p>
            This removes the signature for {d.targetLabel ?? 'the target'} outright, rather than
            reinstating whatever this deployment replaced. Their mail goes out unsigned from the
            next message.
          </p>
          <Banner tone="warn">
            <strong>This does not step back through the version history</strong>
            <div className="tiny">
              Earlier versions stay in the template&apos;s history and can be restored and pushed
              again. Nothing is deleted except the delivery mechanism itself.
            </div>
          </Banner>
        </Modal>
      )}

      {confirmRollback && (
        <Modal
          title={
            d.rollbackEffect === 'remove'
              ? 'Remove the signature'
              : 'Restore the previous version'
          }
          onClose={() => setConfirmRollback(false)}
          footer={
            <>
              <button onClick={() => setConfirmRollback(false)}>Cancel</button>
              <button className="danger" disabled={rollback.pending} onClick={() => void rollback.run()}>
                {rollback.pending
                  ? 'Working…'
                  : d.rollbackEffect === 'remove'
                    ? 'Remove signature'
                    : 'Restore previous version'}
              </button>
            </>
          }
        >
          {d.rollbackEffect === 'remove' ? (
            <p>
              This deployment created the rule, so undoing it <strong>removes the signature</strong>{' '}
              for {d.targetLabel ?? 'the target'} entirely. Their mail goes out unsigned from the
              next message.
            </p>
          ) : (
            <p>
              This deployment replaced an earlier signature, so undoing it{' '}
              <strong>reinstates that earlier version</strong> for{' '}
              {d.targetLabel ?? 'the target'} — it does not remove the signature. Use{' '}
              <strong>Remove signature</strong> if that is what you want.
            </p>
          )}
          <Banner tone="warn">
            <strong>What rollback means for {d.providerKey}</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }} className="tiny">
              {d.providerKey === 'transport-rule' && (
                <li>
                  The transport rule is restored to its previous disclaimer HTML, or removed if this
                  deployment created it. Takes effect on the next message sent.
                </li>
              )}
              {d.providerKey === 'intune-script' && (
                <li>
                  The previous signature packages are republished and the current one removed. Devices
                  revert on their next Intune check-in, not immediately.
                </li>
              )}
              {d.providerKey === 'roaming-signature' && (
                <li>
                  Each consented mailbox has its previous signature written back. Anyone recorded as
                  needing consent cannot be reverted this way.
                </li>
              )}
            </ul>
          </Banner>
          <p className="tiny muted">
            The rollback is recorded as its own deployment, so both the change and the reversal stay in
            the audit trail.
          </p>
        </Modal>
      )}
    </>
  );
}

function ItemHtmlModal({
  deploymentId,
  item,
  onClose,
}: {
  deploymentId: string;
  item: DeploymentItem;
  onClose: () => void;
}): JSX.Element {
  const detail = useResource<{
    upn: string;
    renderedHtml: string | null;
    previousHtml: string | null;
    status: string;
  }>(`/api/deployments/${deploymentId}/items/${item.id}/html`);

  return (
    <Modal title={`Signature delivered to ${item.displayName ?? item.upn}`} onClose={onClose} wide>
      {detail.error && <ErrorState error={detail.error} />}
      {detail.loading && <Loading rows={3} />}
      {detail.data && (
        <>
          {detail.data.renderedHtml ? (
            <>
              <div className="field-label">Rendered</div>
              <iframe
                title="Delivered signature"
                sandbox=""
                srcDoc={detail.data.renderedHtml}
                style={{
                  width: '100%',
                  height: 220,
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  background: '#fff',
                }}
              />
              <details className="mt">
                <summary style={{ cursor: 'pointer' }}>Show the HTML source</summary>
                <pre className="log">{detail.data.renderedHtml}</pre>
              </details>
            </>
          ) : (
            <Empty title="No HTML recorded">
              This row did not reach the render stage, so there is nothing to show.
            </Empty>
          )}

          {detail.data.previousHtml && (
            <details className="mt">
              <summary style={{ cursor: 'pointer' }}>
                Previous signature (what a rollback would restore)
              </summary>
              <pre className="log">{detail.data.previousHtml}</pre>
            </details>
          )}
        </>
      )}
    </Modal>
  );
}
